import type { ProductTypeEnum } from '@ztechapps/common.contract'
import { ProductStatusEnum } from '@ztechapps/common.contract'
import type { DataLoadOptions } from '@ztechapps/webui.input'
import React, { useEffect, useState } from 'react'
import { gql } from '@apollo/client'

const allQuery = gql`
  query allProduct($filter: JSON!, $options: JSON) {
    allProduct(filter: $filter, options: $options) {
      _id
      code
      name
      status
      inventory {
        _id
        quantityOnHand
      }
      price
      hasSubComponents
      requiredComputedQuantityOnHand
      baseUom {
        _id
        symbol
      }
      unitOfMeasurePrecision
    }
  }
`

export type UseProductSelectResult = {
  dataLoadOptionsPartial: ({ filter }: { filter?: Record<any, any> }) => DataLoadOptions
  dataLoadOptionsText: ({ filter }: { filter?: Record<any, any> }) => DataLoadOptions
  selectKey?: string
  setSelectedProductType?: React.Dispatch<React.SetStateAction<ProductTypeEnum | 'All'>>
  selectedProductType?: ProductTypeEnum | 'All'
  filter?: Record<any, any>
}

export const useProductSelect = ({
  defaultProductType,
  filter,
}: {
  defaultProductType?: ProductTypeEnum | 'All'
  filter?: Record<any, any>
} = {}): UseProductSelectResult => {
  const defaultDataLoadOptions = {
    query: allQuery,
    resultFieldName: 'allProduct',
    filter: { status: ProductStatusEnum.Active },
  }

  const dataLoadOptionsPartial = ({ filter }) => {
    return {
      ...defaultDataLoadOptions,
      filter: ({ input }) => {
        return {
          code: { $regex: input, $options: 'i' },
          ...defaultDataLoadOptions.filter,
          ...filter,
        }
      },
      options: () => {
        return { sort: { code: 1 as const } }
      },
    }
  }

  const dataLoadOptionsText = ({ filter }) => {
    return {
      ...defaultDataLoadOptions,
      query: allQuery,
      resultFieldName: 'allProduct',
      filter: ({ input }) => {
        let newFilter = { ...filter, ...defaultDataLoadOptions.filter }
        if (input) {
          newFilter = { $text: { $search: input }, ...newFilter }
        }

        return newFilter
      },
      options: ({ input }) => {
        if (input) return { sort: { searchScore: { $meta: 'textScore' } as const } }
      },
    }
  }

  const [selectedProductType, setSelectedProductType] = useState<ProductTypeEnum | 'All' | undefined>(
    defaultProductType ?? 'All'
  )
  const [selectKey, setSelectKey] = useState<string>(selectedProductType)

  const [filterState, setFilterState] = React.useState(filter)

  React.useEffect(() => {
    setFilterState(filter)
  }, [filter])

  useEffect(() => {
    if (selectedProductType === 'All') {
      setFilterState(filter)
    } else {
      setFilterState({ ...filter, productType: selectedProductType })
    }
    setSelectKey(`${selectedProductType}key`)
  }, [selectedProductType, filter])

  return {
    dataLoadOptionsPartial,
    dataLoadOptionsText,
    setSelectedProductType,
    selectedProductType,
    selectKey,
    filter: filterState,
  }
}
